import React from "react";
import produce from "immer";
import { FormattedHTMLMessage, type InjectedIntl, injectIntl } from "react-intl";

import { getSafe } from "@jslib/shared/utils/getSafe";
import { logger } from "@jslib/shared/utils/logger";

import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  type VerificationService,
  type CollectPersonalInfoViewModel,
  type CustomFormFieldConfig,
  type Organization,
  type FormSelectChoice,
  type State,
  type CollectFieldsResponse,
  type CollectPersonalInfoResponse,
  type FieldDescription,
  type AllStatuses,
} from "../../lib/types/types";
import {
  getAvailableStateChoices,
  orgToOption,
  getStatusLabel,
  handleEmailOnKeyDown,
  submitForm,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
  produceDraftViewModelWithAllRequiredFields,
  getAvailableStatuses,
  getFieldConfigByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { CollegeNameComponent as CollegeName } from "../FormFields/CollegeName/CollegeNameComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { FormFooterComponent as FormFooter } from "../FormFooter/FormFooterComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { FieldIdEnum, SegmentEnum, VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { CityComponent } from "../FormFields/City/City";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { ActiveDutyStartDateComponent } from "../FormFields/ActiveDutyStartDate/ActiveDutyStartDateComponent";
import { BranchOfServiceComponent as BranchOfService } from "../FormFields/BranchOfService/BranchOfServiceComponent";
import { DriverLicenseNumberComponent as DriverLicenseNumber } from "../FormFields/DriverLicenseNumber/DriverLicenseNumberComponent";
import { EBTCardNumberComponent } from "../FormFields/EBTCardNumber/EBTCardNumberComponent";
import { StatusComponent as Status } from "../FormFields/Status/StatusComponent";
import { LowIncomeOrganizationComponent as LowIncomeOrganization } from "../FormFields/LowIncomeOrganization/LowIncomeOrganizationComponent";
import { FirstResponderOrganizationComponent as FirstResponderOrganization } from "../FormFields/FirstResponderOrganization/FirstResponderOrganizationComponent";
import { LicensedProfessionalOrganizationComponent as LicensedProfessionalOrganization } from "../FormFields/LicensedProfessionalOrganization/LicensedProfessionalOrganizationComponent";
import { MedicalProfessionalOrganizationComponent as MedicalProfessionalOrganization } from "../FormFields/MedicalProfessionalOrganization/MedicalProfessionalOrganizationComponent";
import { MemberIdComponent as MemberId } from "../FormFields/MemberId/MemberIdComponent";
import { MembershipOrganizationComponent as MembershipOrganization } from "../FormFields/MembershipOrganization/MembershipOrganizationComponent";
import { TeacherSchoolComponent as TeacherSchool } from "../FormFields/TeacherSchool/TeacherSchoolComponent";
import { SocialSecurityNumber } from "../FormFields/SSN/SSN";
import { DischargeDateComponent } from "../FormFields/DischargeDate/DischargeDateComponent";
import { getAllFieldsToCollect } from "../../lib/ServerApi/VerificationApiClient";
import { getMarketConsent } from "../../lib/ProgramTheme/programThemeGetters";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";

interface StepCollectPersonalInfoProps {
  intl: InjectedIntl;
  verificationService: VerificationService;
}

export const StepCollectPersonalInfo = ({
  intl,
  verificationService,
}: StepCollectPersonalInfoProps) => {
  const viewModel = verificationService.viewModel as CollectPersonalInfoViewModel;
  const { updateViewModel, programTheme } = verificationService;

  const [requiredFields, setRequiredFields] = React.useState<FieldDescription[]>(undefined);

  const isFieldRequired = (key: keyof CollectPersonalInfoViewModel) =>
    requiredFields && requiredFields.some((field) => field.key === key);

  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as CollectPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const updatePersonalInfoViewModel = (key: keyof CollectPersonalInfoViewModel, value: any) => {
    const nextState: CollectPersonalInfoViewModel = produce(
      viewModel,
      (draft: CollectPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    updateViewModel(nextState);
  };

  const updateViewModelWithRequiredFields = (newRequiredFields: FieldDescription[]) => {
    const nextState: CollectPersonalInfoViewModel = produceDraftViewModelWithAllRequiredFields(
      viewModel,
      newRequiredFields,
    );
    updateViewModel(nextState);
  };

  const locale = viewModel.localeChoice.value;
  const options = getOptions();

  const { segment, availableStatuses } = verificationResponse;
  const pascalCaseSegment = segment.charAt(0).toUpperCase() + segment.slice(1);

  const isStatusRequired = (): boolean =>
    availableStatuses &&
    (segment === SegmentEnum.FIRST_RESPONDER ||
      segment === SegmentEnum.MEDICAL ||
      segment === SegmentEnum.LOW_INCOME ||
      segment === SegmentEnum.LICENSED_PROFESSIONAL ||
      segment === SegmentEnum.EMPLOYMENT ||
      segment === SegmentEnum.RECENT_MOVER ||
      segment === SegmentEnum.MILITARY);

  const getAndSetRequiredFields = (requestBody: { statuses?: AllStatuses[] }) =>
    (async () => {
      try {
        const collectFieldsResponse: CollectFieldsResponse = await getAllFieldsToCollect(
          verificationResponse.verificationId,
          verificationResponse.currentStep,
          requestBody,
        );
        const newRequiredFields = getSafe(() => collectFieldsResponse.fieldsToCollect.required, []);

        if (isStatusRequired()) {
          // Adding `status` as a required input field if it's been removed by getting
          // all fields to collect with a statuses req body
          newRequiredFields.push({ key: "status" });
        }

        updateViewModelWithRequiredFields(newRequiredFields);
        setRequiredFields(newRequiredFields);
      } catch (error) {
        logger.error(`Failed to determine fields to collect: ${error}`);
      }
    })();

  const getOrganizationField = () => {
    switch (segment) {
      case SegmentEnum.STUDENT:
        return (
          <CollegeName
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            isErrored={!!fieldValidationErrors.organization}
            isRequired
            disabled={getFieldConfigByFieldId("organization")?.disabled}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
          />
        );
      case SegmentEnum.TEACHER:
        return (
          <TeacherSchool
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            isErrored={!!fieldValidationErrors.organization}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
          />
        );
      case SegmentEnum.MEMBER:
        return (
          <MembershipOrganization
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            isErrored={!!fieldValidationErrors.organization}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
          />
        );
      case SegmentEnum.MILITARY:
        return (
          <BranchOfService
            isErrored={!!fieldValidationErrors.organization}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
            value={{
              value: getSafe(
                () =>
                  (verificationService.viewModel as CollectPersonalInfoViewModel).organization.id,
              ),
              label: getSafe(
                () =>
                  (verificationService.viewModel as CollectPersonalInfoViewModel).organization.name,
              ),
            }}
            verificationService={verificationService}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
          />
        );
      case SegmentEnum.FIRST_RESPONDER:
        return (
          <FirstResponderOrganization
            isErrored={!!fieldValidationErrors.organization}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
          />
        );
      case SegmentEnum.MEDICAL:
        return (
          <MedicalProfessionalOrganization
            isErrored={!!fieldValidationErrors.organization}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
          />
        );
      case SegmentEnum.LICENSED_PROFESSIONAL:
        return (
          <LicensedProfessionalOrganization
            isErrored={!!fieldValidationErrors.organization}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
            value={(verificationService.viewModel as CollectPersonalInfoViewModel).organization}
            verificationService={verificationService}
            disabled={getFieldConfigByFieldId("organization")?.disabled}
          />
        );
      case SegmentEnum.LOW_INCOME:
        return (
          <LowIncomeOrganization
            isErrored={!!fieldValidationErrors.organization}
            onChange={(choice: Organization) => {
              updateViewModelOrganization(choice, verificationService);
              updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
            }}
            value={orgToOption(
              (verificationService.viewModel as CollectPersonalInfoViewModel).organization,
            )}
            verificationService={verificationService}
            isRequired
            disabled={getFieldConfigByFieldId("organization")?.disabled}
          />
        );
      default:
        return null;
    }
  };

  React.useEffect(() => {
    if (availableStatuses && segment === SegmentEnum.MILITARY) {
      // display all required military fields by setting an initial default status
      getAndSetRequiredFields({ statuses: [] });
    } else {
      // fetch initial list of required input fields
      getAndSetRequiredFields({});
    }
  }, []);

  React.useEffect(() => {
    if (!getMarketConsent(programTheme).enabled && requiredFields && requiredFields.length === 0) {
      // skip this step if there are no required fields, and marketConsent is not enabled
      submitForm(viewModel, verificationService, VerificationStepsEnum.collectPersonalInfo);
    }
  }, [requiredFields]);

  React.useEffect(() => {
    if (viewModel.status) {
      // if status changes, re-fetch required fields because they may have changed based on selected status
      getAndSetRequiredFields({ statuses: [viewModel.status] });
    }
  }, [viewModel.status]);

  return (
    <div
      id="sid-step-personal-info-personal-info"
      className="sid-personal-info-container sid-l-container"
    >
      <div role="main">
        <PersonalInfoHeader
          verificationService={verificationService}
          classNameIdentifier="personal-info"
        />

        <CountryComponentWrapper
          verificationService={verificationService}
          disabled={getFieldConfigByFieldId("country")?.disabled}
        />

        {isStatusRequired() && (
          <Status
            segmentId={segment}
            value={{
              value: viewModel.status,
              label: getStatusLabel(intl, viewModel.status, null),
            }}
            isErrored={!!fieldValidationErrors.status}
            errorId={`invalid${pascalCaseSegment}Status`}
            options={getAvailableStatuses(intl, availableStatuses)}
            disabled={getFieldConfigByFieldId("status")?.disabled}
            onChange={(status: FormSelectChoice) => {
              updatePersonalInfoViewModel("status", status ? (status.value as string) : "");
              updateFieldValidationErrorsByFieldId(
                "status",
                status ? (status.value as string) : "",
                verificationService,
              );
            }}
          />
        )}

        {isFieldRequired("organization") && getOrganizationField()}

        <div className="sid-names">
          {isFieldRequired("firstName") && (
            <FirstName
              value={viewModel.firstName}
              isErrored={!!fieldValidationErrors.firstName}
              disabled={getFieldConfigByFieldId("firstName")?.disabled}
              onChange={(newValue) => {
                updatePersonalInfoViewModel("firstName", newValue);
                updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
              }}
            />
          )}

          {isFieldRequired("lastName") && (
            <LastName
              value={viewModel.lastName}
              isErrored={!!fieldValidationErrors.lastName}
              disabled={getFieldConfigByFieldId("lastName")?.disabled}
              onChange={(newValue) => {
                updatePersonalInfoViewModel("lastName", newValue);
                updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
              }}
            />
          )}
        </div>

        {isFieldRequired("birthDate") && (
          <BirthDate
            value={viewModel.birthDate}
            errorId={fieldValidationErrors.birthDate}
            locale={locale}
            isRequired
            disabled={getFieldConfigByFieldId("birthDate")?.disabled}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("birthDate", newValue);
              updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
            }}
          />
        )}

        {isFieldRequired("email") && (
          <Email
            value={viewModel.email}
            isErrored={!!fieldValidationErrors.email}
            explanation=" "
            onChange={(newValue) => {
              updatePersonalInfoViewModel("email", newValue);
              updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
            }}
            onKeyDown={(event) => handleEmailOnKeyDown(event)}
            disabled={getFieldConfigByFieldId("email")?.disabled}
          />
        )}

        {isFieldRequired("phoneNumber") && (
          <PhoneNumber
            isRequired={!!verificationService.programTheme.smsLoopEnabled}
            value={viewModel.phoneNumber}
            isErrored={!!fieldValidationErrors.phoneNumber}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("phoneNumber", newValue);
              updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
            }}
            selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
            disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
          />
        )}

        {isFieldRequired("address1") && (
          <AddressComponent
            value={viewModel.address1}
            isRequired
            isErrored={!!fieldValidationErrors.address1}
            errorId={fieldValidationErrors.address1}
            disabled={getFieldConfigByFieldId("address1")?.disabled}
            onChange={(value) => {
              updatePersonalInfoViewModel("address1", value);
              updateFieldValidationErrorsByFieldId("address1", value, verificationService);
            }}
          />
        )}

        {isFieldRequired("city") && (
          <CityComponent
            value={viewModel.city}
            isRequired
            isErrored={!!fieldValidationErrors.city}
            errorId={fieldValidationErrors.city}
            disabled={getFieldConfigByFieldId("city")?.disabled}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("city", newValue);
              updateFieldValidationErrorsByFieldId("city", newValue, verificationService);
            }}
          />
        )}

        {isFieldRequired("dischargeDate") && (
          <DischargeDateComponent
            errorId={fieldValidationErrors.dischargeDate}
            locale={locale}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("dischargeDate", newValue);
              updateFieldValidationErrorsByFieldId("dischargeDate", newValue, verificationService);
            }}
            value={viewModel.dischargeDate}
            disabled={getFieldConfigByFieldId("dischargeDate")?.disabled}
          />
        )}

        {isFieldRequired("driverLicenseNumber") && (
          <DriverLicenseNumber
            value={viewModel.driverLicenseNumber}
            isErrored={!!fieldValidationErrors.driverLicenseNumber}
            isRequired
            disabled={getFieldConfigByFieldId("driverLicenseNumber")?.disabled}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("driverLicenseNumber", newValue);
              updateFieldValidationErrorsByFieldId(
                "driverLicenseNumber",
                newValue,
                verificationService,
              );
            }}
          />
        )}

        {isFieldRequired("ebtCardNumber") && (
          <EBTCardNumberComponent
            value={viewModel.ebtCardNumber}
            isErrored={!!fieldValidationErrors.ebtCardNumber}
            disabled={getFieldConfigByFieldId("ebtCardNumber")?.disabled}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("ebtCardNumber", newValue);
              updateFieldValidationErrorsByFieldId("ebtCardNumber", newValue, verificationService);
            }}
          />
        )}

        {isFieldRequired("memberId") && (
          <MemberId
            value={viewModel.memberId}
            isErrored={!!fieldValidationErrors.memberId}
            isRequired={getFieldConfigByFieldId("memberId")?.requireIfOptional}
            disabled={getFieldConfigByFieldId("memberId")?.disabled}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("memberId", newValue);
              updateFieldValidationErrorsByFieldId("memberId", newValue, verificationService);
            }}
            label={
              <FormattedHTMLMessage
                id="step.collectMedicalPersonalInfo.registrationNumberLabel"
                defaultMessage="Registration Number"
              />
            }
          />
        )}

        {isFieldRequired("postalCode") && (
          <PostalCodeComponent
            isErrored={!!fieldValidationErrors.postalCode}
            onChange={(newValue) => {
              updatePersonalInfoViewModel("postalCode", newValue);
              updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
            }}
            value={viewModel.postalCode}
            disabled={getFieldConfigByFieldId("postalCode")?.disabled}
          />
        )}

        {isFieldRequired("socialSecurityNumber") && (
          <div className="sid-ssn-wrapper">
            <SocialSecurityNumber
              value={viewModel.socialSecurityNumber}
              onChange={(value: string) => {
                updatePersonalInfoViewModel("socialSecurityNumber", value);
                updateFieldValidationErrorsByFieldId(
                  "socialSecurityNumber",
                  value,
                  verificationService,
                );
              }}
              isErrored={!!fieldValidationErrors[FieldIdEnum.socialSecurityNumber]}
              placeholder="000-00-0000"
              disabled={getFieldConfigByFieldId("socialSecurityNumber")?.disabled}
            />
          </div>
        )}

        {isFieldRequired("state") && (
          <StateSelectComponent
            isRequired
            options={getAvailableStateChoices(verificationService.programTheme, intl)}
            value={viewModel.state || undefined}
            isErrored={Boolean(fieldValidationErrors.state)}
            onChange={async (stateChoice: FormSelectChoice<State, string>) => {
              const stateValue = stateChoice ? stateChoice.value : undefined;
              updatePersonalInfoViewModel("state", stateValue);
              updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
            }}
            label={<FormattedHTMLMessage id="state" defaultMessage="State" />}
            disabled={getFieldConfigByFieldId("state")?.disabled}
          />
        )}

        {isFieldRequired("activeDutyStartDate") &&
          verificationService.programTheme.strictMilitaryValidationEnabled && (
            <ActiveDutyStartDateComponent
              errorId={fieldValidationErrors.activeDutyStartDate}
              locale={locale}
              onChange={(newValue) => {
                updatePersonalInfoViewModel("activeDutyStartDate", newValue);
                updateFieldValidationErrorsByFieldId(
                  "activeDutyStartDate",
                  newValue,
                  verificationService,
                );
              }}
              value={viewModel.activeDutyStartDate}
              disabled={getFieldConfigByFieldId("activeDutyStartDate")?.disabled}
            />
          )}

        {options.customFormFields?.map((config: CustomFormFieldConfig) => (
          <FormFieldCustom config={config} verificationService={verificationService} />
        ))}

        <MarketConsentWrapper
          verificationService={verificationService}
          isErrored={!!fieldValidationErrors.marketConsentValue}
          onChange={(newValue) => {
            updatePersonalInfoViewModel("metadata", {
              ...verificationService.viewModel.metadata,
              marketConsentValue: newValue,
            });
            updateFieldValidationErrorsByFieldId(
              "marketConsentValue",
              newValue,
              verificationService,
            );
          }}
          viewModel={viewModel}
          disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
        />

        <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

        <CaptchaComponent
          verificationService={verificationService}
          isErrored={!!fieldValidationErrors.captchaToken}
        />

        <PersonalInfoStepSubmitButton
          step={VerificationStepsEnum.collectPersonalInfo}
          verificationService={verificationService}
          viewModel={viewModel}
          failedInstantMatch={failedInstantMatch}
          buttonText={
            <FormattedHTMLMessage
              id="step.personalInfo.submitButtonText"
              defaultMessage="Verify and continue"
            />
          }
        />
      </div>
      <FormFooter verificationService={verificationService} />
    </div>
  );
};

export const StepCollectPersonalInfoComponent = injectIntl(StepCollectPersonalInfo);
